"use client";

import { motion, type Transition, useMotionValue } from "framer-motion";
import Image, { StaticImageData } from "next/image";
import { useCallback, useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import { Card } from "../card/card";

const AUTO_DELAY_MS = 5000;
const DRAG_BUFFER = 50;
const SPRING_OPTIONS = {
  type: "spring",
  mass: 3,
  stiffness: 300,
  damping: 60,
} satisfies Transition;

type ImageSliderProps = {
  images: StaticImageData[];
  activeIndex: number;
  onChange: (index: number) => void;
};

export default function ImageSlider({ images, activeIndex, onChange }: ImageSliderProps) {
  const dragX = useMotionValue(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);

  const nextImage = useCallback(() => {
    onChange((activeIndex + 1) % images.length);
  }, [activeIndex, images.length, onChange]);

  const resetInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (dragX.get() === 0) {
        nextImage();
      }
    }, AUTO_DELAY_MS);
  }, [dragX, nextImage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          resetInterval();
          observer.disconnect();
        }
      },
      { threshold: 0.7 }
    );

    if (sliderRef.current) {
      observer.observe(sliderRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [resetInterval]);

  const handleDragEnd = () => {
    resetInterval();
    const x = dragX.get();

    if (x <= -DRAG_BUFFER && activeIndex < images.length - 1) {
      onChange(activeIndex + 1);
    } else if (x >= DRAG_BUFFER && activeIndex > 0) {
      onChange(activeIndex - 1);
    }
  };

  return (
    <Card ref={sliderRef} className="relative bg-primary-50 border-primary-400 p-4 w-full max-w-xl mx-auto space-y-4 overflow-hidden">
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        style={{ x: dragX }}
        animate={{ translateX: `-${activeIndex * 100}%` }}
        transition={SPRING_OPTIONS}
        onDragEnd={handleDragEnd}
        className="flex cursor-grab items-center active:cursor-grabbing"
      >
        {images.map((imgSrc, idx) => (
          <motion.div
            key={idx}
            animate={{
              opacity: activeIndex === idx ? 1 : 0.5,
              scale: activeIndex === idx ? 1 : 0.85,
            }}
            transition={SPRING_OPTIONS}
            className="w-full shrink-0 rounded-lg border border-primary-400 overflow-hidden"
          >
            <Image src={imgSrc} alt={`Image ${idx + 1}`} className="w-full" draggable={false} />
          </motion.div>
        ))}
      </motion.div>
      <div className="w-full flex justify-center gap-2">
        {images.map((_, idx) => (
          <button
            key={idx}
            type="button"
            aria-label={`Go to image ${idx + 1}`}
            onClick={() => {
              resetInterval();
              onChange(idx);
            }}
            className={cn(
              "h-3 w-3 cursor-pointer rounded-full transition-colors border border-primary-400 outline-none focus-visible:outline-primary",
              idx === activeIndex ? "bg-primary-400" : "bg-white"
            )}
          />
        ))}
      </div>
    </Card>
  );
}
